import { motion } from "framer-motion";

const HERO_TILES = [
  {
    src: "https://customer-assets.emergentagent.com/job_discount-retail-hub-1/artifacts/59482b72_pot.png",
    alt: "Ceramic cookware set with gold handles",
    retail: "$164.99",
    price: "$119.99",
    save: "Save 27%",
    badge: "Overstock",
    tone: "gold",
    tag: "In Stock",
    scale: 1.3,
  },
  {
    src: "https://customer-assets.emergentagent.com/job_discount-retail-hub-1/artifacts/mud2rmo3_laptop-no-bg-preview%20%28carve.photos%29.png",
    alt: "HP laptop",
    retail: "$1,649.99",
    price: "$1,299.99",
    save: "Save 21%",
    badge: "Best Deal",
    tone: "dark",
    tag: "Online Only",
    scale: 1.12,
  },
  {
    src: "https://customer-assets.emergentagent.com/job_discount-retail-hub-1/artifacts/mygngb4l_coffee.png",
    alt: "Automatic espresso machine",
    retail: "$449.99",
    price: "$349.99",
    save: "Save 22%",
    badge: "Limited Stock",
    tone: "red",
    tag: "Warehouse Pick",
    scale: 1.05,
  },
  {
    src: "https://customer-assets.emergentagent.com/job_discount-retail-hub-1/artifacts/qs7acfvf_2571c6e5-ff1d-44a8-9411-39be2ab1d5de.png",
    alt: "Home cleaning essentials bundle",
    retail: "$199.99",
    price: "$145.99",
    save: "Save 27%",
    badge: "Save up to 27%",
    tone: "green",
    tag: "Limited Quantity",
    pad: "p-0.5",
    scale: 1.3,
  },
];

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 26 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay: d, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
  return (
    <section id="top" className="relative pt-20 lg:min-h-screen lg:flex lg:items-center" data-testid="hero-section">
      <div className="w-full mx-auto max-w-7xl px-5 sm:px-8 py-10 lg:py-12">
        <div className="grid lg:grid-cols-[1fr_1.05fr] gap-12 lg:gap-20 items-center">
          <div>
            <motion.h1
              {...fade(0.05)}
              className="font-heading text-5xl sm:text-6xl lg:text-[64px] font-medium tracking-tight leading-[0.95] text-[#1A1A1A]"
            >
              Everything for
              <br />
              every home
            </motion.h1>

            <motion.p
              {...fade(0.15)}
              className="font-body text-lg md:text-xl text-[#6B6B66] mt-[26px] lg:mt-9 leading-relaxed max-w-xl"
            >
              Trusted brands. Exceptional value. Everyday essentials — sourced
              from Costco overstock, stock overruns and{" "}
              <br className="hidden lg:block" />
              customer returns.
            </motion.p>

            <motion.div {...fade(0.25)} className="mt-5 flex flex-col sm:flex-row gap-4">
              <a
                href="#contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-2 rounded-[5px] bg-[#F8B808] px-[23px] h-10 font-body text-base font-medium text-[#1A1A1A] transition-all hover:bg-[#DD9F00] active:scale-[0.98]"
              >
                Request Catalog
              </a>
            </motion.div>

            <motion.div {...fade(0.4)} className="mt-7">
              <p className="font-body text-[11px] font-semibold uppercase tracking-[0.18em] text-[#B5B0A6] mb-3">
                Featured Trusted Brands
              </p>
              <div className="font-heading text-lg font-medium tracking-tight text-[#BDBAB3] select-none leading-relaxed space-y-1">
                <p>
                  {["Samsung", "LG", "Ninja", "KitchenAid", "Philips", "De'Longhi", "Dyson", "Shark"].join("  ·  ")}
                </p>
                <p>
                  {["Cuisinart", "HP", "Sony", "Apple", "Whirlpool", "Bosch", "GE", "& more"].join("  ·  ")}
                </p>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {HERO_TILES.map((t, i) => (
                <div
                  key={t.alt}
                  data-testid={`hero-product-${i}`}
                  className="group flex flex-col overflow-hidden rounded-[8px] border border-[#E5E5E5] bg-white"
                >
                  <div className="relative aspect-[1/0.96] overflow-hidden bg-white">
                    <span
                      className="absolute top-2.5 left-2.5 z-10 rounded-[3px] bg-[#F8B808] pl-2 pr-3 py-1 font-body text-[10px] font-bold uppercase tracking-wide text-[#1A1A1A]"
                      style={{ clipPath: "polygon(0 0, 100% 0, calc(100% - 8px) 100%, 0 100%)" }}
                    >
                      {t.badge}
                    </span>
                    <div
                      className="h-full w-full"
                      style={{ transform: `scale(${t.scale || 1})` }}
                    >
                      <img
                        src={t.src}
                        alt={t.alt}
                        className={`h-full w-full object-contain ${t.pad || "p-1"}`}
                        loading="eager"
                      />
                    </div>
                  </div>
                  <div className="px-3 pb-2 sm:px-3.5 sm:pb-2 pt-1.5">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <span className="order-1 basis-full sm:basis-auto font-heading text-xl sm:text-2xl font-bold text-[#1A1A1A]">{t.price}</span>
                      <span className="order-2 font-body text-xs text-[#9A958C] line-through">{t.retail}</span>
                      <span className="order-3 sm:ml-auto w-fit shrink-0 rounded-md bg-[#FBF4DD] px-1.5 sm:px-2 py-0.5 font-body text-[11px] font-bold text-[#9A6A00]">
                        {t.save}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
